// Comments thread on the call-detail page (2026-05-08).
//
// Renders the existing comments oldest-first, plus an input at the
// bottom for posting new ones. Inputs accept @ to trigger an
// autocomplete dropdown of company teammates — picking one inserts
// a "@Name" pill in the textarea, and the resolved userId is sent
// alongside the body so the backend can fan out notifications.
//
// Backend endpoints (see routes/demoComments.js):
//   GET    /api/demos/:id/comments
//   POST   /api/demos/:id/comments  { body, mentions: [userId,…] }
//   PATCH  /api/demos/:id/comments/:cid
//   DELETE /api/demos/:id/comments/:cid

import { useEffect, useMemo, useRef, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

function fmtRelative(s) {
  if (!s) return '';
  try {
    const ms = Date.now() - new Date(s).getTime();
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return new Date(s).toLocaleDateString();
  } catch { return ''; }
}

function authedFetch(url, authToken, opts = {}) {
  return fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
      ...(opts.headers || {}),
    },
  });
}

/**
 * Render a comment body with @Name pills inline. Bodies are stored
 * as plain text — pills are detected by matching the substring
 * "@<name>" against the comment's `mentionedUserIds` (resolved via
 * the team roster prop).
 */
function renderBodyWithMentions(body, teamById) {
  if (!body) return null;
  const out = [];
  // Split on @-runs but keep them; a name can contain spaces so we
  // greedy-match the longest known name from teamById prefixed by @.
  // Simpler heuristic: tokenize on word-or-@ boundaries.
  const knownNames = [...teamById.values()]
    .map((u) => u.fullName || u.email)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length); // longest first so "Daniel Fellows" matches before "Daniel"

  let cursor = 0;
  while (cursor < body.length) {
    const at = body.indexOf('@', cursor);
    if (at === -1) {
      out.push(body.slice(cursor));
      break;
    }
    if (at > cursor) out.push(body.slice(cursor, at));
    let matched = null;
    for (const name of knownNames) {
      if (body.slice(at + 1, at + 1 + name.length).toLowerCase() === name.toLowerCase()) {
        matched = name;
        break;
      }
    }
    if (matched) {
      out.push(
        <span
          key={`mention-${at}`}
          style={{
            display: 'inline-block',
            background: 'rgba(99, 102, 241, 0.12)',
            color: '#4338ca',
            padding: '1px 6px',
            borderRadius: 4,
            fontWeight: 600,
            fontSize: 'inherit',
          }}
        >
          @{matched}
        </span>
      );
      cursor = at + 1 + matched.length;
    } else {
      out.push('@');
      cursor = at + 1;
    }
  }
  return out;
}

export default function CommentsThread({ demoId, authToken, currentUser, onCountChange }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [posting, setPosting] = useState(false);
  const [draft, setDraft] = useState('');
  // Tracks which user IDs have been @mentioned in the current draft.
  // Set, not array, for cheap idempotent inserts.
  const [draftMentions, setDraftMentions] = useState(() => new Set());
  const [team, setTeam] = useState([]);
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionAnchor, setMentionAnchor] = useState(0); // index of the '@' in the textarea
  const textareaRef = useRef(null);

  const teamById = useMemo(() => {
    const m = new Map();
    for (const u of team) m.set(u.id, u);
    return m;
  }, [team]);

  // Bubble the live count up to the parent so it can render a badge
  // on the Comments tab. Fires on every comments-state change
  // (initial load, post, delete) so the badge stays in sync.
  useEffect(() => {
    if (typeof onCountChange === 'function') onCountChange(comments.length);
  }, [comments, onCountChange]);

  // Initial load — comments + team roster (for @mention picker).
  useEffect(() => {
    if (!demoId || !authToken) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      authedFetch(`${API_BASE}/api/demos/${encodeURIComponent(demoId)}/comments`, authToken)
        .then((r) => r.ok ? r.json() : { comments: [] }),
      authedFetch(`${API_BASE}/api/permissions/users`, authToken)
        .then((r) => r.ok ? r.json() : { members: [] }),
    ])
      .then(([c, t]) => {
        if (cancelled) return;
        setComments(Array.isArray(c.comments) ? c.comments : []);
        setTeam(Array.isArray(t.members) ? t.members : []);
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [demoId, authToken]);

  // Detect '@' as the user types. We track the last '@' position, then
  // build the mention query from any chars typed after it (until a
  // space terminates).
  const onDraftChange = (e) => {
    const value = e.target.value;
    setDraft(value);
    const cursorPos = e.target.selectionStart;
    // Walk back from cursor looking for '@' before whitespace.
    let i = cursorPos - 1;
    while (i >= 0 && value[i] !== '@' && !/\s/.test(value[i])) i -= 1;
    if (i >= 0 && value[i] === '@') {
      const tail = value.slice(i + 1, cursorPos);
      setShowMentionPicker(true);
      setMentionQuery(tail);
      setMentionAnchor(i);
    } else {
      setShowMentionPicker(false);
      setMentionQuery('');
    }
  };

  const filteredTeam = useMemo(() => {
    const q = mentionQuery.toLowerCase();
    return team
      .filter((u) => {
        const name = (u.fullName || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      })
      .slice(0, 6);
  }, [team, mentionQuery]);

  const insertMention = (user) => {
    const name = user.fullName || user.email;
    const before = draft.slice(0, mentionAnchor);
    const after = draft.slice(mentionAnchor + 1 + mentionQuery.length);
    const newDraft = `${before}@${name} ${after}`;
    setDraft(newDraft);
    setDraftMentions((prev) => {
      const next = new Set(prev);
      next.add(user.id);
      return next;
    });
    setShowMentionPicker(false);
    setMentionQuery('');
    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = before.length + 1 + name.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const onSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed || posting) return;
    setPosting(true);
    setError(null);
    try {
      // Filter draftMentions to only those whose name still appears
      // in the draft body (user may have backspaced over a pill).
      const presentMentions = [...draftMentions].filter((id) => {
        const u = teamById.get(id);
        if (!u) return false;
        const name = u.fullName || u.email;
        return name && trimmed.includes(`@${name}`);
      });
      const res = await authedFetch(
        `${API_BASE}/api/demos/${encodeURIComponent(demoId)}/comments`,
        authToken,
        { method: 'POST', body: JSON.stringify({ body: trimmed, mentions: presentMentions }) }
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `Post failed (${res.status})`);
      }
      const data = await res.json();
      setComments((prev) => [...prev, data.comment]);
      setDraft('');
      setDraftMentions(new Set());
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  const onDelete = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      const res = await authedFetch(
        `${API_BASE}/api/demos/${encodeURIComponent(demoId)}/comments/${commentId}`,
        authToken,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `Delete failed (${res.status})`);
      }
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      window.alert(`Couldn't delete: ${err.message}`);
    }
  };

  const currentUserId = currentUser ? currentUser.id : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
      {error && (
        <div style={{
          padding: '10px 12px', borderRadius: 8,
          background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626',
          fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {/* Thread */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loading && <div style={{ color: 'var(--room-text-subtle, #94a3b8)', fontSize: 13 }}>Loading…</div>}
        {!loading && comments.length === 0 && (
          <div style={{
            padding: 16, textAlign: 'center',
            color: 'var(--room-text-subtle, #94a3b8)', fontSize: 13, lineHeight: 1.5,
          }}>
            No comments yet. Drop the first one — use <code>@</code> to mention a teammate.
          </div>
        )}
        {comments.map((c) => {
          const isMine = c.authorUserId === currentUserId;
          return (
            <div
              key={c.id}
              style={{
                display: 'flex', flexDirection: 'column', gap: 4,
                padding: 12,
                borderRadius: 10,
                border: '1px solid var(--room-border, #e2e8f0)',
                background: 'var(--room-card, #ffffff)',
              }}
            >
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 8,
                fontSize: 12,
              }}>
                <strong style={{ color: 'var(--room-text-strong, #0f172a)' }}>{c.authorName || c.authorEmail || 'Unknown'}</strong>
                <span style={{ color: 'var(--room-text-subtle, #94a3b8)' }}>{fmtRelative(c.createdAt)}</span>
                {c.edited && <span style={{ color: 'var(--room-text-subtle, #94a3b8)', fontStyle: 'italic' }}>(edited)</span>}
                {isMine && (
                  <button
                    type="button"
                    onClick={() => onDelete(c.id)}
                    style={{
                      marginLeft: 'auto',
                      background: 'transparent', border: 'none',
                      color: 'var(--room-text-subtle, #94a3b8)', cursor: 'pointer',
                      fontSize: 11, fontFamily: 'inherit',
                    }}
                    title="Delete this comment"
                  >
                    Delete
                  </button>
                )}
              </div>
              <div style={{
                fontSize: 14, lineHeight: 1.5, color: 'var(--room-text-strong, #0f172a)',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {renderBodyWithMentions(c.body, teamById)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <form onSubmit={onSubmit} style={{ marginTop: 'auto', position: 'relative' }}>
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={onDraftChange}
          placeholder="Add a comment… use @ to mention a teammate"
          rows={3}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--room-border, #e2e8f0)',
            borderRadius: 10,
            fontSize: 14,
            fontFamily: 'inherit',
            resize: 'vertical',
            minHeight: 70,
            boxSizing: 'border-box',
          }}
        />
        {showMentionPicker && filteredTeam.length > 0 && (
          <div style={{
            position: 'absolute',
            bottom: '100%', marginBottom: 4,
            left: 0,
            background: 'var(--room-card, #ffffff)',
            border: '1px solid var(--room-border, #e2e8f0)',
            borderRadius: 8,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.06)',
            minWidth: 220,
            maxWidth: 320,
            zIndex: 50,
            overflow: 'hidden',
          }}>
            {filteredTeam.map((u) => (
              <button
                key={u.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
                style={{
                  display: 'block', width: '100%',
                  padding: '8px 12px', textAlign: 'left',
                  border: 'none', background: 'transparent',
                  cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--room-surface, #f8fafc)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ fontWeight: 600, color: 'var(--room-text-strong, #0f172a)' }}>
                  {u.fullName || u.email}
                </div>
                {u.fullName && u.email && (
                  <div style={{ fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)' }}>{u.email}</div>
                )}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            type="submit"
            disabled={!draft.trim() || posting}
            style={{
              padding: '8px 16px',
              border: 'none', borderRadius: 8,
              background: !draft.trim() || posting ? 'rgba(99, 102, 241, 0.4)' : '#6366f1',
              color: 'white', fontSize: 13, fontWeight: 600,
              fontFamily: 'inherit',
              cursor: !draft.trim() || posting ? 'not-allowed' : 'pointer',
            }}
          >
            {posting ? 'Posting…' : 'Post comment'}
          </button>
        </div>
      </form>
    </div>
  );
}
